/**
 * PURPOSE: Overlay de casillas de firma sobre el PDF de la Vista Planilla FEDUP (FTR-0062), dentro del mismo contenedor que el iframe.
 * RESPONSIBILITIES:
 *  - Sí: con un modo activo, cubrir el iframe con una capa propia (el visor nativo no recibe clics, ni rueda ni Ctrl+rueda: no puede
 *    cambiar su zoom y desalinear las casillas) y dibujar cada casilla del modo en su rectángulo real: contorno punteado verde, pulso
 *    leve (sin animación con prefers-reduced-motion) y un check pequeño en las ya firmadas.
 *  - Sí: escalar y desplazarse con el zoom y el scroll del visor (vive dentro del envoltorio del iframe; la geometría sale de la hoja).
 *  - Sí: mostrar la firma recién guardada (o tapar la recién quitada) hasta que llega el PDF regenerado.
 *  - Sí: con el partido oficializado, casillas sin resaltar y, al pulsar, un candado con "Partido oficializado: …" (sin pad).
 *  - No: no pide nada al backend ni decide si se usa (eso lo hace el visor según el motor de PDF).
 * COLLABORATORS:
 *  - FedupPreviewModal.jsx, fedupSignatures.utils.js
 * ROUTE: src/components/FullScreenModal/FullScreenPlanilla/shared/fedupSignatures/FedupSignatureOverlay.jsx
 */

import React, { useEffect } from "react";
import { FiCheck, FiLock } from "react-icons/fi";

import { OFFICIAL_LOCK_MESSAGE, boxToFrameRect, isSlotLocked, signatureImageRect, slotsOfMode } from "./fedupSignatures.utils.js";

const PULSE_CSS = `
@keyframes fedupSignaturePulse { 0%, 100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.45); } 50% { box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.12); } }
.fedup-signature-zone { animation: fedupSignaturePulse 1.8s ease-in-out infinite; }
@media (prefers-reduced-motion: reduce) { .fedup-signature-zone { animation: none; } }
`;

/** Firma recién guardada (o casilla recién vaciada) dentro de su zona, mientras se regenera el PDF. */
function InstantPreview({ slot, preview, map }) {
  const box = slot.box;
  const ink = slot.inkBox || box;
  if (preview?.removed) {
    return (
      <span
        aria-hidden="true"
        className="pointer-events-none absolute bg-white"
        style={{
          left: (ink.x0 - box.x0) * map.scale,
          top: (ink.y0 - box.y0) * map.scale,
          width: (ink.x1 - ink.x0) * map.scale,
          height: (ink.y1 - ink.y0) * map.scale,
        }}
      />
    );
  }
  if (!preview?.dataUrl) return null;
  const r = signatureImageRect(ink, preview.width || 1, preview.height || 1);
  return (
    <img
      alt=""
      aria-hidden="true"
      data-fedup-signature-preview={slot.slot}
      src={preview.dataUrl}
      className="pointer-events-none absolute select-none"
      style={{ left: (r.x0 - box.x0) * map.scale, top: (r.y0 - box.y0) * map.scale, width: (r.x1 - r.x0) * map.scale, height: (r.y1 - r.y0) * map.scale }}
      draggable={false}
    />
  );
}

function LockPopover({ rect, frameHeight, onDismiss }) {
  useEffect(() => {
    const timer = window.setTimeout(() => onDismiss?.(), 5000);
    return () => window.clearTimeout(timer);
  }, [onDismiss]);
  const below = rect.top + rect.height + 64 < frameHeight;
  return (
    <div
      role="status"
      aria-live="polite"
      data-fedup-signature-lock="true"
      className="pointer-events-none absolute z-20 flex max-w-[260px] items-start gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[11px] font-semibold text-slate-700 shadow-lg"
      style={{ left: Math.max(4, rect.left), top: below ? rect.top + rect.height + 6 : Math.max(4, rect.top - 56) }}
    >
      <FiLock className="mt-0.5 shrink-0 text-slate-500" aria-hidden="true" />
      <span>{OFFICIAL_LOCK_MESSAGE}</span>
    </div>
  );
}

export default function FedupSignatureOverlay({ status, mode, map, frameHeight = 0, official = false, previews = {}, onSelectSlot, lockSlotId = null, onDismissLock }) {
  if (!mode || !map) return null;
  const slots = slotsOfMode(status, mode);
  const lockSlot = lockSlotId ? slots.find((s) => s.slot === lockSlotId) : null;

  return (
    <div
      className="absolute inset-0 z-10"
      data-fedup-signature-overlay={mode}
      onClick={(event) => {
        if (event.target === event.currentTarget) onDismissLock?.();
      }}
    >
      <style>{PULSE_CSS}</style>
      {slots.map((slot) => {
        const rect = boxToFrameRect(slot.box, map);
        const signed = Boolean(slot.signature);
        const locked = isSlotLocked(slot, official);
        const state = locked ? "firma bloqueada (partido oficializado)" : signed ? "firmada" : "sin firma";
        return (
          <button
            key={slot.slot}
            type="button"
            data-fedup-signature-slot={slot.slot}
            data-signed={signed ? "true" : "false"}
            aria-label={`${slot.label}: ${state}`}
            title={locked ? OFFICIAL_LOCK_MESSAGE : signed ? `${slot.label}: firmada (pulsa para rehacer o quitar)` : `${slot.label}: firmar`}
            onClick={() => onSelectSlot?.(slot.slot)}
            className={[
              "absolute rounded-[3px] p-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500",
              locked
                ? "cursor-default border-0 bg-transparent"
                : "fedup-signature-zone cursor-pointer border-2 border-dotted border-emerald-500 bg-emerald-400/10 transition-colors hover:bg-emerald-400/20",
            ].join(" ")}
            style={{ left: rect.left, top: rect.top, width: rect.width, height: rect.height }}
          >
            <InstantPreview slot={slot} preview={previews[slot.slot]} map={map} />
            {signed && !locked ? (
              <span
                aria-hidden="true"
                data-fedup-signature-check="true"
                className="pointer-events-none absolute -right-1.5 -top-1.5 inline-flex h-3.5 w-3.5 items-center justify-center rounded-full bg-emerald-600 text-[9px] text-white shadow"
              >
                <FiCheck />
              </span>
            ) : null}
          </button>
        );
      })}
      {lockSlot ? <LockPopover rect={boxToFrameRect(lockSlot.box, map)} frameHeight={frameHeight} onDismiss={onDismissLock} /> : null}
    </div>
  );
}
